import { useState, useRef, useEffect } from "react";
import type { AccountSummary } from "../../services/portfolioService";

interface AccountSelectorProps {
  accounts: AccountSummary[];
  selectedAccountId: string | null; // null = "All Accounts"
  onSelectAccount: (accountId: string | null) => void;
  totalEquity: string;  // String from API
  todayChange?: string;
  todayChangePct: string;
}

export function AccountSelector({
  accounts,
  selectedAccountId,
  onSelectAccount,
  totalEquity,
  todayChangePct,
}: AccountSelectorProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const formatCurrency = (value: string | number | undefined | null) => {
    const num = typeof value === "string" ? parseFloat(value) : (typeof value === "number" ? value : 0);
    if (isNaN(num)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatPercent = (value: string | number | undefined | null) => {
    const num = typeof value === "string" ? parseFloat(value) : (typeof value === "number" ? value : 0);
    if (isNaN(num)) return "+0.00%";
    const sign = num >= 0 ? "+" : "";
    return `${sign}${num.toFixed(2)}%`;
  };

  const parseNum = (value: string | number | undefined | null): number => {
    if (typeof value === "string") return parseFloat(value) || 0;
    if (typeof value === "number") return value;
    return 0;
  };

  const selectedAccount = selectedAccountId
    ? accounts.find((a) => a.id === selectedAccountId)
    : null;

  const displayName = selectedAccount ? selectedAccount.account_name : "All Accounts";
  const displayEquity = selectedAccount ? selectedAccount.portfolio_value : totalEquity;
  const displayChange = selectedAccount ? selectedAccount.today_change_pct : todayChangePct;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-4 py-2.5 rounded-xl bg-surface-800/80 border border-surface-700/50 hover:border-surface-600 transition-all group"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500/20 to-accent-500/20 flex items-center justify-center">
            <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-surface-100">{displayName}</span>
              {selectedAccount && (
                <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${selectedAccount.fractional_enabled !== false ? "bg-emerald-500/20 text-emerald-400" : "bg-amber-500/20 text-amber-400"}`}>
                  {selectedAccount.fractional_enabled !== false ? "Fractional" : "Whole Shares"}
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5 text-xs">
              <span className="text-surface-400">{formatCurrency(displayEquity)}</span>
              <span className={`${parseNum(displayChange) >= 0 ? "text-green-400" : "text-red-400"}`}>
                {formatPercent(displayChange)}
              </span>
            </div>
          </div>
        </div>
        <svg
          className={`w-4 h-4 text-surface-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-72 py-2 rounded-xl bg-surface-800 border border-surface-700/50 shadow-xl shadow-black/20 z-50">
          {/* All Accounts Option */}
          <button
            onClick={() => {
              onSelectAccount(null);
              setIsOpen(false);
            }}
            className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-700/50 transition-colors ${
              selectedAccountId === null ? "bg-primary-500/10" : ""
            }`}
          >
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-surface-100">All Accounts</div>
              <div className="flex items-center gap-1.5 text-xs">
                <span className="text-surface-400">{formatCurrency(totalEquity)}</span>
                <span className={`${parseNum(todayChangePct) >= 0 ? "text-green-400" : "text-red-400"}`}>
                  {formatPercent(todayChangePct)}
                </span>
              </div>
            </div>
            {selectedAccountId === null && (
              <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </button>

          <div className="my-2 border-t border-surface-700/50" />

          {/* Individual Accounts */}
          {accounts.map((account) => (
            <button
              key={account.id}
              onClick={() => {
                onSelectAccount(account.id);
                setIsOpen(false);
              }}
              className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-surface-700/50 transition-colors ${
                selectedAccountId === account.id ? "bg-primary-500/10" : ""
              }`}
            >
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  account.account_type === "live"
                    ? "bg-green-500/20"
                    : "bg-amber-500/20"
                }`}
              >
                {account.account_type === "live" ? (
                  <svg className="w-4 h-4 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                )}
              </div>
              <div className="flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-surface-100">{account.account_name}</span>
                  <span
                    className={`px-1.5 py-0.5 text-xs rounded ${
                      account.account_type === "live"
                        ? "bg-green-500/20 text-green-400"
                        : "bg-amber-500/20 text-amber-400"
                    }`}
                  >
                    {account.account_type}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-surface-400">{formatCurrency(account.portfolio_value)}</span>
                  <span className={`${parseNum(account.today_change_pct) >= 0 ? "text-green-400" : "text-red-400"}`}>
                    {formatPercent(account.today_change_pct)}
                  </span>
                  <span className="text-surface-500">• {account.investment_count} strategies</span>
                  <span className={`${account.fractional_enabled !== false ? "text-emerald-500" : "text-amber-500"}`}>
                    • {account.fractional_enabled !== false ? "Fractional" : "Whole"}
                  </span>
                </div>
              </div>
              {selectedAccountId === account.id && (
                <svg className="w-4 h-4 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
